/**
 * Read-only React Query hook for the platform skill catalog (P4.T3).
 *
 * Fetches GET /api/skills (P4.T3 backend) and caches the result for
 * 5 minutes — the catalog rarely changes mid-session (a fresh backend
 * boot is needed to pick up new SKILL.md files anyway).
 *
 * The SkillsCard and the SkillPickerModal both consume this hook;
 * they share a single RQ cache entry keyed by skillCatalogQueryKey.
 *
 * Future tickets that add workspace-authored skill creation/import
 * will invalidate this query on success to refresh the picker.
 */
import { useQuery, type UseQueryResult } from '@tanstack/react-query'
import { apiClient } from '@/services/apiClient'

export interface SkillCatalogEntry {
  id: string
  name: string
  display_name: string
  description: string
  version: string
  scope: string
  required_capabilities: string[]
  languages: string[]
  deprecated: boolean
}

export const skillCatalogQueryKey = ['skills', 'catalog'] as const

export function useSkillCatalog(): UseQueryResult<SkillCatalogEntry[], Error> {
  return useQuery<SkillCatalogEntry[], Error>({
    queryKey: skillCatalogQueryKey,
    queryFn: () => apiClient.get<SkillCatalogEntry[]>('/api/skills'),
    staleTime: 5 * 60_000,
  })
}
